import { TagsType } from "@components/Common/HomeworkTemplate/Modals/types";
import useEnqueueToast from "@lib/hooks/useEnqueueToast";
import { pullAt } from "lodash";
import { useCallback, useMemo, useState } from "react";

type UseTagManagerParams = {
  tagsList: TagsType[];
  handleGetTagList: () => TagsType[];
  handleGetTagToRemoveIds: () => string[];
  handleSetTagsValue: (newTags: TagsType[]) => void;
  handleSetTagToRemoveIds: (newTagToRemove: string[]) => void;
};

export const useAddTagsFieldView = ({
  tagsList,
  handleGetTagList,
  handleGetTagToRemoveIds,
  handleSetTagsValue,
  handleSetTagToRemoveIds,
}: UseTagManagerParams) => {
  const {
    actions: { showWarning },
  } = useEnqueueToast();
  const [tagInputValue, setTagInputValue] = useState<TagsType | null>(null);
  const [isTagEditing, setIsTagEditing] = useState<boolean>(false);

  const sortedTagsList = useMemo(() => {
    return tagsList.sort((a, b) => (a.kind === "system" ? -1 : 1));
  }, [tagsList]);

  const isDuplicateTag = useCallback(
    (tagName: string) => handleGetTagList().some((tag) => tag.name === tagName),
    [handleGetTagList],
  );

  const addNewTag = useCallback(
    (newTag: TagsType) => {
      if (newTag.name === "") {
        showWarning("タグ名を入力してください。", "empty");
        return;
      }
      if (isDuplicateTag(newTag.name)) {
        showWarning(`${newTag.name}はすでに存在します。`, "uniq");
        setIsTagEditing(false);
        setTagInputValue(null);
        return;
      }
      const newTags = [...handleGetTagList(), newTag];
      handleSetTagsValue(newTags);
      setTagInputValue(null);
      setIsTagEditing(false);
    },
    [isDuplicateTag, handleGetTagList, handleSetTagsValue, showWarning],
  );

  const handleTagInputChange = useCallback(
    (value: string, shouldSave = false) => {
      const newTag = { dbId: null, name: value };
      setTagInputValue(newTag);
      if (shouldSave) {
        addNewTag(newTag);
      }
    },
    [addNewTag],
  );

  const handleRemoveTag = useCallback(
    (index: number, tag: TagsType) => {
      const tagsListCopy = handleGetTagList();
      if (tag?.dbId) {
        const newTagToRemove = [...handleGetTagToRemoveIds(), tag.dbId];
        handleSetTagToRemoveIds(newTagToRemove);
      }
      pullAt(tagsListCopy, index);
      handleSetTagsValue(tagsListCopy);
    },
    [
      handleGetTagList,
      handleGetTagToRemoveIds,
      handleSetTagToRemoveIds,
      handleSetTagsValue,
    ],
  );

  return {
    sortedTagsList,
    tagInputValue,
    isTagEditing,
    setIsTagEditing,
    handleTagInputChange,
    handleRemoveTag,
    addNewTag,
  };
};
